import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid } from "recharts";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Statistics() {
  const [ov, setOv] = useState(null);
  const [det, setDet] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/stats/overview").then((r) => setOv(r.data));
    api.get("/stats/detailed").then((r) => setDet(r.data));
  };
  useEffect(() => { load(); }, []);

  const resetKasse = async () => {
    if (txt !== "RESET") return toast.error("Bitte RESET eingeben, um zu bestätigen");
    setBusy(true);
    try {
      const { data } = await api.post("/pos/reset", { confirm: "RESET" });
      toast.success(`Kasse zurückgesetzt · ${Object.values(data.cleared).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0)} Einträge gelöscht`);
      setConfirm(false); setTxt("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!ov || !det) return <div className="text-[#A8A398]">Lade …</div>;

  return (
    <div className="space-y-8" data-testid="statistics-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Analytics</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Statistiken</h1>
        </div>
        <button data-testid="pos-reset-btn" onClick={() => setConfirm(true)}
          className="text-xs uppercase tracking-widest border border-[#5B1319] text-[#EE9CA1] rounded px-3 py-2 hover:bg-[#5B1319]/20 flex items-center gap-2 transitions-safe">
          <AlertTriangle size={14} /> Kasse zurücksetzen
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Tickets verkauft" value={ov.tickets_sold} />
        <Stat label="Eingecheckt" value={ov.checked_in} />
        <Stat label="Ticketumsatz" value={`${ov.ticket_revenue.toFixed(2)} €`} />
        <Stat label="Snackumsatz" value={`${ov.snack_revenue.toFixed(2)} €`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard title="Umsatz je Tag (30 Tage)">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={det.by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1A1A1A" />
              <XAxis dataKey="date" stroke="#A8A398" fontSize={11} />
              <YAxis stroke="#A8A398" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #2A2A2A" }} />
              <Line type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Beliebteste Filme">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={det.popular_movies}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1A1A1A" />
              <XAxis dataKey="movie" stroke="#A8A398" fontSize={10} interval={0} angle={-20} height={60} />
              <YAxis stroke="#A8A398" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #2A2A2A" }} />
              <Bar dataKey="tickets" fill="#D4AF37" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Beliebteste Sitzplätze">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={det.popular_seats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1A1A1A" />
              <XAxis dataKey="seat" stroke="#A8A398" fontSize={11} />
              <YAxis stroke="#A8A398" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #2A2A2A" }} />
              <Bar dataKey="count" fill="#5B1319" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Meistverkaufte Snacks">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={det.top_snacks}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1A1A1A" />
              <XAxis dataKey="name" stroke="#A8A398" fontSize={10} interval={0} angle={-25} height={70} />
              <YAxis stroke="#A8A398" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #2A2A2A" }} />
              <Bar dataKey="qty" fill="#4A90E2" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <ResetKasseDialog
        open={confirm}
        txt={txt}
        setTxt={setTxt}
        busy={busy}
        onConfirm={resetKasse}
        onClose={() => { setConfirm(false); setTxt(""); }}
      />
    </div>
  );
}
function Stat({ label, value }) {
  return (
    <div className="card-lux p-4">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#A8A398]">{label}</div>
      <div className="mt-2 text-2xl font-heading">{value}</div>
    </div>
  );
}
function ChartCard({ title, children }) {
  return (
    <div className="card-lux p-4">
      <div className="font-heading text-xl mb-3">{title}</div>
      {children}
    </div>
  );
}

function ResetKasseDialog({ open, txt, setTxt, busy, onConfirm, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#0A0A0A] border border-[#5B1319] rounded-xl p-6 space-y-4" data-testid="pos-reset-dialog">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#EE9CA1] flex items-center gap-2">
          ⚠️ Achtung — Kasse zurücksetzen
        </div>
        <div className="text-sm text-[#F5F0E6] leading-relaxed">
          Diese Aktion löscht <b>alle</b> Bestellungen, Tickets, Scans, Reservierungen und Snack-Bestellungen und setzt Besuchszähler zurück. Filme, Vorstellungen, Säle, Produkte und Gäste bleiben erhalten. Die Kasse startet danach bei <span className="text-[#D4AF37] font-mono">0 €</span>.
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Zur Bestätigung &bdquo;RESET&ldquo; eingeben</label>
          <input value={txt} onChange={(e) => setTxt(e.target.value)}
            data-testid="pos-reset-input"
            placeholder="RESET"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#5B1319] outline-none font-mono uppercase tracking-widest" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#A8A398]">Abbrechen</button>
          <button data-testid="pos-reset-confirm" onClick={onConfirm} disabled={busy || txt !== "RESET"}
            className="bg-[#5B1319] text-[#F5F0E6] px-6 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#7A1921] disabled:opacity-40">
            {busy ? "Setze zurück …" : "Endgültig zurücksetzen"}
          </button>
        </div>
      </div>
    </div>
  );
}
